import { oc } from "@orpc/contract";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Custom (org-scoped, DB-stored) field resolvers + built-in source catalog.
// Powers the "Input Resolvers" tab in the Sales Order Review UI settings card.
// ---------------------------------------------------------------------------

/** snake_case key, same shape as built-in resolver names. */
const ResolverNameSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(
    /^[a-z][a-z0-9_]*$/,
    "Resolver names must be snake_case (lowercase letters, digits, underscores)",
  );

/** Mirrors the code-sandbox workflow step's max code size. */
const ResolverCodeSchema = z.string().min(1).max(100_000);

const ResolverDependenciesSchema = z.array(z.string().min(1)).max(20);

/**
 * 'display' — review-page field resolvers (computedData); 'submission' —
 * ERP submission payload resolvers (Input Submission Map `resolver` sources).
 */
export const UiFieldResolverKindSchema = z.enum(["display", "submission"]);

export const BuiltinResolverSourceSchema = z.object({
  name: z.string(),
  sourcePath: z.string(),
  description: z.string(),
  dependencies: z.array(z.string()),
  clonable: z.boolean(),
  code: z.string(),
  kind: UiFieldResolverKindSchema.optional().default("display"),
});

export const CustomUiFieldResolverSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  code: z.string(),
  dependencies: z.array(z.string()),
  enabled: z.boolean(),
  clonedFrom: z.string().nullable(),
  kind: UiFieldResolverKindSchema.optional().default("display"),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateUiFieldResolverInputSchema = z.object({
  name: ResolverNameSchema,
  code: ResolverCodeSchema,
  description: z.string().max(2_000).optional(),
  dependencies: ResolverDependenciesSchema.optional(),
  /** Built-in resolver name this snippet was cloned from, if any. */
  clonedFrom: z.string().max(255).optional(),
  kind: UiFieldResolverKindSchema.optional(),
});

export const UpdateUiFieldResolverInputSchema = z.object({
  id: z.string().uuid(),
  name: ResolverNameSchema.optional(),
  code: ResolverCodeSchema.optional(),
  description: z.string().max(2_000).nullable().optional(),
  dependencies: ResolverDependenciesSchema.optional(),
  enabled: z.boolean().optional(),
});

export const DeleteUiFieldResolverInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Live sample of the resolver context data bag, built from the org's most
 * recent drafts so admins can see what `ctx.data` looks like when authoring
 * resolver code. `paths` is the breadth-merged dot-path inventory across the
 * sampled drafts; `sample` is a representative (depth-limited, truncated)
 * merged object for JSON display.
 */
export const SampleResolverContextResponseSchema = z.object({
  sample: z.record(z.string(), z.unknown()).nullable(),
  paths: z.array(
    z.object({
      path: z.string(),
      example: z.string().nullable(),
    }),
  ),
  /** How many recent drafts were sampled (0 = org has no drafts yet). */
  sampledDraftCount: z.number(),
});

export const TestRunUiFieldResolverInputSchema = z.object({
  /** Snippet to execute — typically the editor's current (possibly unsaved) code. */
  code: ResolverCodeSchema,
  /** ctx.fieldId to run with (multi-field resolvers switch on it). */
  fieldId: z.string().min(1).max(255).optional(),
  /**
   * 'submission' runs the snippet against the submission scopes (the ctx an
   * Input Submission Map `resolver` source receives) instead of the display
   * resolver bag.
   */
  kind: UiFieldResolverKindSchema.optional(),
  /**
   * For submission kind: run with the line-scope ctx of this line (0-based)
   * instead of the header scope.
   */
  lineIndex: z.number().int().nonnegative().optional(),
});

export const TestRunUiFieldResolverResponseSchema = z.object({
  ok: z.boolean(),
  /** FieldResolverResult ({ value, options?, fieldOptions? }) when ok. */
  result: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  durationMs: z.number(),
  /** The draft whose real resolver context the snippet ran against. */
  sampledDraft: z
    .object({
      id: z.string(),
      purchaseOrderNumber: z.string().nullable(),
    })
    .nullable(),
});

export const uiFieldResolversContracts = {
  /** The 18 code-registered resolvers with source snapshots / clonable snippets. */
  listBuiltins: oc
    .input(z.object({}))
    .output(z.object({ builtins: z.array(BuiltinResolverSourceSchema) }))
    .route({ method: "GET" }),
  /** The org's custom (DB-stored) resolvers. */
  list: oc
    .input(z.object({ kind: UiFieldResolverKindSchema.optional() }))
    .output(z.object({ resolvers: z.array(CustomUiFieldResolverSchema) }))
    .route({ method: "GET" }),
  create: oc
    .input(CreateUiFieldResolverInputSchema)
    .output(CustomUiFieldResolverSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateUiFieldResolverInputSchema)
    .output(CustomUiFieldResolverSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteUiFieldResolverInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
  sampleContext: oc
    .input(z.object({}))
    .output(SampleResolverContextResponseSchema)
    .route({ method: "GET" }),
  /** Execute a snippet against the org's most recent draft's real ctx. */
  testRun: oc
    .input(TestRunUiFieldResolverInputSchema)
    .output(TestRunUiFieldResolverResponseSchema)
    .route({ method: "POST" }),
} as const;

export type BuiltinResolverSourceEntry = z.infer<
  typeof BuiltinResolverSourceSchema
>;
export type CustomUiFieldResolver = z.infer<typeof CustomUiFieldResolverSchema>;
export type CreateUiFieldResolverInput = z.infer<
  typeof CreateUiFieldResolverInputSchema
>;
export type UpdateUiFieldResolverInput = z.infer<
  typeof UpdateUiFieldResolverInputSchema
>;
export type SampleResolverContextResponse = z.infer<
  typeof SampleResolverContextResponseSchema
>;
export type TestRunUiFieldResolverResponse = z.infer<
  typeof TestRunUiFieldResolverResponseSchema
>;
